/**
 * src/components/PulseDetail.tsx
 *
 * Drill-down drawer for a single modelKey (Chase 2.1 / 2.2 / 2.5).
 * Opened from the Category Pulse table — lets Chase validate a number:
 *   · how the anchor was built (per source, with n + confidence)
 *   · the raw eBay sold rows behind the median (cheapest-first, where the
 *     £1-game / accessory contamination shows up)
 *   · how this modelKey's listings were filtered
 */

import { num, txt } from '@/lib/format';
import { useQuery } from '@tanstack/react-query';
import { Loader2, X } from 'lucide-react';
import { getModelKeyDetail, getModelKeyTrace, type TraceStage } from '@/api/chase';
import { cn } from '@/lib/cn';

const gbp = (n: number | null) => (n == null ? '—' : `£${n.toLocaleString()}`);

const STAGE_LABEL: Record<string, string> = {
  sold_anchor_rows: 'eBay sold (anchor pool)',
  listings_seen: 'Listings seen',
  scored: 'Scored',
  emitted: 'Emitted',
  held: 'Held',
  dropped: 'Dropped',
  hot: 'Marked hot',
  delivered: 'Delivered to users',
};
const STAGE_CLS: Record<string, string> = {
  emitted: 'text-emerald-600',
  dropped: 'text-rose-500',
  held: 'text-amber-600',
  delivered: 'text-sky-600',
};

function TraceFunnel({ modelKey }: { modelKey: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['chase-trace', modelKey],
    queryFn: () => getModelKeyTrace(modelKey),
    staleTime: 30_000,
  });
  if (isLoading) return <div className="text-slate-400 text-sm flex items-center"><Loader2 className="animate-spin mr-2" size={14} /> tracing…</div>;
  if (!data) return <p className="text-sm text-slate-400">No trace available.</p>;
  const max = Math.max(1, ...data.stages.map((s: TraceStage) => s.count));
  return (
    <div className="space-y-1.5">
      {data.stages.map((s: TraceStage) => (
        <div key={s.stage} className="flex items-center gap-2 text-sm">
          <div className="w-40 shrink-0 text-right text-xs text-slate-500">{STAGE_LABEL[s.stage] ?? s.stage}</div>
          <div className="flex-1 h-5 bg-slate-100 dark:bg-slate-800 rounded overflow-hidden">
            <div className={cn('h-full rounded', s.stage === 'dropped' ? 'bg-rose-400/50' : s.stage === 'emitted' ? 'bg-emerald-400/50' : 'bg-sky-400/40')}
              style={{ width: `${Math.round((s.count / max) * 100)}%` }} />
          </div>
          <div className={cn('w-14 text-right tabular-nums font-medium', STAGE_CLS[s.stage] ?? '')}>{s.count.toLocaleString()}</div>
        </div>
      ))}
      {data.dropReasons.length > 0 && (
        <div className="text-[11px] text-slate-400 pt-1">
          drop reasons: {data.dropReasons.map(r => `${r.reason} (${r.count})`).join(' · ')}
        </div>
      )}
    </div>
  );
}

const FLAG_CLS: Record<string, string> = {
  passed: 'text-emerald-600',
  anchor_excluded: 'text-amber-600',
  regex_flagged: 'text-rose-600',
  hard_dropped: 'text-rose-500',
  held: 'text-slate-500',
  other: 'text-slate-400',
};

export default function PulseDetail({ modelKey, onClose }: { modelKey: string; onClose: () => void }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['chase-modelkey', modelKey],
    queryFn: () => getModelKeyDetail(modelKey),
    staleTime: 30_000,
  });

  return (
    <div className="fixed inset-0 z-40 flex justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white dark:bg-slate-900 shadow-xl overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-4 py-3 flex items-center justify-between">
          <div>
            <div className="font-mono text-sm font-semibold">{modelKey}</div>
            {typeof data?.category === 'string' && data.category && <div className="text-xs text-slate-400">{data.category}</div>}
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="animate-spin mr-2" size={18} /> Loading…</div>
        ) : isError || !data ? (
          <div className="py-16 text-center text-rose-500 text-sm">Failed to load detail.</div>
        ) : (
          <div className="p-4 space-y-6">
            {/* 2.1 anchor breakdown */}
            <section>
              <h3 className="text-sm font-semibold mb-2">Anchor breakdown</h3>
              {data.anchor ? (
                <div className="space-y-2">
                  <div className="flex items-baseline gap-2 text-sm">
                    <span className="text-slate-400">final</span>
                    <span className="text-lg font-semibold">{gbp(data.anchor.final)}</span>
                    {data.anchor.winner && <span className="text-xs text-slate-400">via {data.anchor.winner}{typeof data.anchor.confidence === 'number' ? ` · conf ${num(data.anchor.confidence, 2)}` : ''}</span>}
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[11px] uppercase tracking-wide text-slate-400 text-left border-b border-slate-200 dark:border-slate-800">
                        <th className="py-1">source</th><th className="py-1 text-right">price</th><th className="py-1 text-right">n</th><th className="py-1 text-right">confidence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.anchor.sources.map(s => (
                        <tr key={s.source} className="border-b border-slate-100 dark:border-slate-800/60">
                          <td className="py-1">{s.source}</td>
                          <td className="py-1 text-right tabular-nums">{gbp(s.price)}</td>
                          <td className="py-1 text-right tabular-nums text-slate-500">{s.sampleCount ?? '—'}</td>
                          <td className="py-1 text-right tabular-nums text-slate-500">{num(s.confidence, 2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <p className="text-sm text-slate-400">No anchor data found for this modelKey.</p>}
            </section>

            {/* 2.7 pipeline trace */}
            <section>
              <h3 className="text-sm font-semibold mb-2">Pipeline trace <span className="font-normal text-slate-400">(scraped → scored → emitted → delivered)</span></h3>
              <TraceFunnel modelKey={modelKey} />
            </section>

            {/* 2.5 filter flags */}
            <section>
              <h3 className="text-sm font-semibold mb-2">Filter status (last 30d listings)</h3>
              {data.filterFlags.length ? (
                <div className="flex flex-wrap gap-3 text-sm">
                  {data.filterFlags.map(f => (
                    <div key={f.status} className="border border-slate-200 dark:border-slate-800 rounded px-2.5 py-1.5">
                      <span className={cn('font-semibold', FLAG_CLS[f.status] ?? 'text-slate-500')}>{f.status}</span>
                      <span className="ml-2 tabular-nums">{f.count}</span>
                      {f.sampleReasons.length > 0 && <div className="text-[11px] text-slate-400 mt-0.5">{f.sampleReasons.join(', ')}</div>}
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-slate-400">No deals for this modelKey in the last 30 days.</p>}
            </section>

            {/* 2.2 raw sold drill-down */}
            <section>
              <h3 className="text-sm font-semibold mb-2">Raw eBay sold listings · {data.soldCount} in 30d <span className="font-normal text-slate-400">(cheapest first — contamination shows here)</span></h3>
              {data.soldRaw.length ? (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[11px] uppercase tracking-wide text-slate-400 text-left border-b border-slate-200 dark:border-slate-800">
                      <th className="py-1 text-right pr-2">price</th><th className="py-1">title</th><th className="py-1">cond</th><th className="py-1 text-right">sold</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.soldRaw.map((r, i) => (
                      <tr key={i} className="border-b border-slate-100 dark:border-slate-800/60">
                        <td className="py-1 text-right pr-2 tabular-nums font-medium">{gbp(r.price)}</td>
                        <td className="py-1 max-w-sm truncate" title={r.title ?? ''}>
                          {r.url ? <a href={r.url} target="_blank" rel="noreferrer" className="hover:underline">{r.title ?? '(no title)'}</a> : (r.title ?? '(no title)')}
                        </td>
                        <td className="py-1 text-slate-500">{txt(r.condition)}</td>
                        <td className="py-1 text-right text-slate-400">{r.ts ? new Date(r.ts).toLocaleDateString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <p className="text-sm text-slate-400">No sold listings in the last 30 days.</p>}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
